/**
 * 音频工具类
 * 用于播放各种系统音效
 */
class AudioManager {
  constructor() {
    this.audioContext = null
    this.sounds = new Map()
    this.volume = 0.6
    this.enabled = true
    
    // 初始化音频上下文
    this.initAudioContext()
  }

  /**
   * 初始化音频上下文
   */
  initAudioContext() {
    try {
      // 创建音频上下文
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
      
      // 预生成各种音效
      this.generateSuccessSound()
      this.generateErrorSound()
      this.generateWarningSound()
      this.generateInfoSound()
      this.generateLoginSuccessSound()
    } catch (error) {
      console.warn('音频上下文初始化失败:', error)
      this.enabled = false
    }
  }

  /**
   * 生成登录成功音效（特别优化的愉悦音效）
   */
  generateLoginSuccessSound() {
    if (!this.audioContext) return

    this.sounds.set('loginSuccess', () => {
      // 创建一个更复杂、更愉悦的登录成功音效
      const now = this.audioContext.currentTime
      
      // 主旋律 - 上升的大三和弦琶音
      const mainMelody = [
        { freq: 523.25, time: 0, duration: 0.3 },    // C5
        { freq: 659.25, time: 0.15, duration: 0.3 }, // E5
        { freq: 783.99, time: 0.3, duration: 0.3 },  // G5
        { freq: 1046.5, time: 0.45, duration: 0.4 }  // C6
      ]
      
      // 和声背景
      const harmony = [
        { freq: 261.63, time: 0, duration: 0.8 },    // C4
        { freq: 329.63, time: 0.2, duration: 0.6 },  // E4
        { freq: 392.00, time: 0.4, duration: 0.4 }   // G4
      ]
      
      // 播放主旋律
      mainMelody.forEach(note => {
        const oscillator = this.audioContext.createOscillator()
        const gainNode = this.audioContext.createGain()
        const filterNode = this.audioContext.createBiquadFilter()
        
        // 音频链路：振荡器 -> 滤波器 -> 增益 -> 输出
        oscillator.connect(filterNode)
        filterNode.connect(gainNode)
        gainNode.connect(this.audioContext.destination)
        
        // 设置振荡器
        oscillator.frequency.setValueAtTime(note.freq, now + note.time)
        oscillator.type = 'sine'
        
        // 设置滤波器（让声音更温暖）
        filterNode.type = 'lowpass'
        filterNode.frequency.setValueAtTime(2000, now + note.time)
        filterNode.Q.setValueAtTime(1, now + note.time)
        
        // 音量包络（ADSR）
        const startTime = now + note.time
        const endTime = startTime + note.duration
        
        gainNode.gain.setValueAtTime(0, startTime)
        gainNode.gain.linearRampToValueAtTime(this.volume * 0.4, startTime + 0.05) // Attack
        gainNode.gain.linearRampToValueAtTime(this.volume * 0.3, startTime + 0.1)  // Decay
        gainNode.gain.setValueAtTime(this.volume * 0.3, endTime - 0.1)             // Sustain
        gainNode.gain.exponentialRampToValueAtTime(0.001, endTime)                 // Release
        
        oscillator.start(startTime)
        oscillator.stop(endTime)
      })
      
      // 播放和声背景（音量更低）
      harmony.forEach(note => {
        const oscillator = this.audioContext.createOscillator()
        const gainNode = this.audioContext.createGain()
        
        oscillator.connect(gainNode)
        gainNode.connect(this.audioContext.destination)
        
        oscillator.frequency.setValueAtTime(note.freq, now + note.time)
        oscillator.type = 'triangle'
        
        const startTime = now + note.time
        const endTime = startTime + note.duration
        
        gainNode.gain.setValueAtTime(0, startTime)
        gainNode.gain.linearRampToValueAtTime(this.volume * 0.15, startTime + 0.1)
        gainNode.gain.exponentialRampToValueAtTime(0.001, endTime)
        
        oscillator.start(startTime)
        oscillator.stop(endTime)
      })
      
      // 添加一个轻微的钟声效果
      const bellOscillator = this.audioContext.createOscillator()
      const bellGain = this.audioContext.createGain()
      
      bellOscillator.connect(bellGain)
      bellGain.connect(this.audioContext.destination)
      
      bellOscillator.frequency.setValueAtTime(1568, now + 0.6) // G6
      bellOscillator.type = 'sine'
      
      bellGain.gain.setValueAtTime(0, now + 0.6)
      bellGain.gain.linearRampToValueAtTime(this.volume * 0.2, now + 0.65)
      bellGain.gain.exponentialRampToValueAtTime(0.001, now + 1.1)
      
      bellOscillator.start(now + 0.6)
      bellOscillator.stop(now + 1.1)
    })
  }

  /**
   * 生成成功音效（简单版本）
   */
  generateSuccessSound() {
    if (!this.audioContext) return

    const frequencies = [523.25, 659.25, 783.99] // C5, E5, G5 大三和弦
    
    this.sounds.set('success', () => {
      frequencies.forEach((freq, index) => {
        const oscillator = this.audioContext.createOscillator()
        const gainNode = this.audioContext.createGain()
        
        oscillator.connect(gainNode)
        gainNode.connect(this.audioContext.destination)
        
        oscillator.frequency.setValueAtTime(freq, this.audioContext.currentTime)
        oscillator.type = 'sine'
        
        // 音量包络
        const startTime = this.audioContext.currentTime + index * 0.1
        gainNode.gain.setValueAtTime(0, startTime)
        gainNode.gain.linearRampToValueAtTime(this.volume * 0.3, startTime + 0.05)
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.6)
        
        oscillator.start(startTime)
        oscillator.stop(startTime + 0.6)
      })
    })
  }

  /**
   * 生成错误音效（柔和版本）
   */
  generateErrorSound() {
    if (!this.audioContext) return

    this.sounds.set('error', () => {
      const now = this.audioContext.currentTime
      
      // 创建两个音符的柔和错误提示音
      const notes = [
        { freq: 392, time: 0, duration: 0.15 },    // G4 - 第一个音符
        { freq: 349.23, time: 0.1, duration: 0.2 } // F4 - 第二个音符（下降）
      ]
      
      notes.forEach(note => {
        const oscillator = this.audioContext.createOscillator()
        const gainNode = this.audioContext.createGain()
        const filterNode = this.audioContext.createBiquadFilter()
        
        // 音频链路：振荡器 -> 滤波器 -> 增益 -> 输出
        oscillator.connect(filterNode)
        filterNode.connect(gainNode)
        gainNode.connect(this.audioContext.destination)
        
        // 使用正弦波，更柔和
        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(note.freq, now + note.time)
        
        // 低通滤波器，让声音更温和
        filterNode.type = 'lowpass'
        filterNode.frequency.setValueAtTime(1000, now + note.time)
        filterNode.Q.setValueAtTime(0.5, now + note.time)
        
        // 柔和的音量包络
        const startTime = now + note.time
        const endTime = startTime + note.duration
        
        gainNode.gain.setValueAtTime(0, startTime)
        gainNode.gain.linearRampToValueAtTime(this.volume * 0.25, startTime + 0.02) // 更低的音量
        gainNode.gain.exponentialRampToValueAtTime(0.001, endTime)
        
        oscillator.start(startTime)
        oscillator.stop(endTime)
      })
    })
  }

  /**
   * 生成警告音效
   */
  generateWarningSound() {
    if (!this.audioContext) return

    this.sounds.set('warning', () => {
      const oscillator = this.audioContext.createOscillator()
      const gainNode = this.audioContext.createGain()
      
      oscillator.connect(gainNode)
      gainNode.connect(this.audioContext.destination)
      
      oscillator.frequency.setValueAtTime(440, this.audioContext.currentTime)
      oscillator.frequency.setValueAtTime(554.37, this.audioContext.currentTime + 0.15)
      oscillator.type = 'triangle'
      
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime)
      gainNode.gain.linearRampToValueAtTime(this.volume * 0.3, this.audioContext.currentTime + 0.05)
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3)
      
      oscillator.start()
      oscillator.stop(this.audioContext.currentTime + 0.3)
    })
  }

  /**
   * 生成信息音效
   */
  generateInfoSound() {
    if (!this.audioContext) return

    this.sounds.set('info', () => {
      const oscillator = this.audioContext.createOscillator()
      const gainNode = this.audioContext.createGain()
      
      oscillator.connect(gainNode)
      gainNode.connect(this.audioContext.destination)
      
      oscillator.frequency.setValueAtTime(880, this.audioContext.currentTime)
      oscillator.type = 'sine'
      
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime)
      gainNode.gain.linearRampToValueAtTime(this.volume * 0.2, this.audioContext.currentTime + 0.05)
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.2)
      
      oscillator.start()
      oscillator.stop(this.audioContext.currentTime + 0.2)
    })
  }

  /**
   * 播放指定类型的音效
   * @param {String} type 音效类型
   */
  play(type = 'success') {
    if (!this.enabled || !this.audioContext) return

    try {
      // 恢复音频上下文（某些浏览器需要用户交互后才能播放音频）
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume()
      }

      const soundGenerator = this.sounds.get(type)
      if (soundGenerator) {
        soundGenerator()
      }
    } catch (error) {
      console.warn('音效播放失败:', error)
    }
  }

  /**
   * 播放登录成功音效（特别优化版本）
   */
  playLoginSuccess() {
    this.play('loginSuccess')
  }

  /**
   * 播放成功音效
   */
  playSuccess() {
    this.play('success')
  }

  /**
   * 播放错误音效
   */
  playError() {
    this.play('error')
  }

  /**
   * 播放警告音效
   */
  playWarning() {
    this.play('warning')
  }

  /**
   * 播放信息音效
   */
  playInfo() {
    this.play('info')
  }

  /**
   * 设置音量
   * @param {Number} volume 音量 (0-1)
   */
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume))
  }

  /**
   * 启用/禁用音效
   * @param {Boolean} enabled 是否启用
   */
  setEnabled(enabled) {
    this.enabled = enabled
  }

  /**
   * 获取音效状态
   */
  isEnabled() {
    return this.enabled && !!this.audioContext
  }
}

// 创建全局音频管理器实例
const audioManager = new AudioManager()

// Vue插件
const AudioPlugin = {
  install(Vue) {
    Vue.prototype.$audio = audioManager
    Vue.audio = audioManager
  }
}

export default AudioPlugin
export { audioManager }